import React from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

// Grouping function
const groupByStrategy = (data) => {
  return data.reduce((acc, item) => {
    const strategy = item.StrategyName;
    if (!acc[strategy]) acc[strategy] = [];
    acc[strategy].push(item);
    return acc;
  }, {});
};

// Helper to calculate totals
const getGroupTotals = (group) => {
  return group.reduce(
    (totals, item) => {
      totals.total_pnl += item.total_pnl || 0;
      totals.lmv += item.LMV || 0;
      totals.smv += item.SMV || 0;
      return totals;
    },
    { total_pnl: 0, lmv: 0, smv: 0 },
  );
};

// Define columns to show
const tableColumns = [
  "security_name",
  "SYMBOL",
  "Security_Type",
  "pricing_source",
  "market_value",
  "LMV",
  "SMV",
  "realtime_quantity",
  "net_trading_quantity",
  "sod_quantity",
  "current_price",
  "last_closing_price",
  "mtm_pnl",
  "trading_pnl",
  "total_pnl",
  "buy_trade_quantity",
  "buy_trade_price",
  "buy_trade_MV",
  "sell_trade_quantity",
  "sell_trade_price",
  "sell_trade_MV",
  "cover_trade_quantity",
  "cover_trade_price",
  "cover_trade_MV",
  "short_trade_quantity",
  "short_trade_price",
  "short_trade_MV",
  "current_fx_rate",
  "risk_to_downside",
];

const formatValue = (value) => {
  if (typeof value === "number") {
    return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
  }
  if (value === null || value === undefined) return "-";
  return value.toString();
};

// Component
const GroupedStrategyView = ({ data }) => {
  const groupedData = groupByStrategy(data);

  return (
    <div>
      {Object.entries(groupedData).map(([strategy, items]) => {
        const totals = getGroupTotals(items);

        return (
          <Accordion key={strategy} defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box
                sx={{ display: "flex", flexDirection: "column", width: "100%" }}
              >
                <Typography
                  variant="h5"
                  sx={{ fontSize: "medium", fontWeight: "bolder", mb: 0.5 }}
                >
                  {strategy}
                </Typography>
                <Box sx={{ display: "flex", gap: 4, mt: 0.5, ml: 1 }}>
                  <Typography variant="body2">
                    <strong>Total P&L:</strong>{" "}
                    {totals.total_pnl.toLocaleString(undefined, {
                      maximumFractionDigits: 2,
                    })}
                  </Typography>
                  <Typography variant="body2">
                    <strong>LMV:</strong>{" "}
                    {totals.lmv.toLocaleString(undefined, {
                      maximumFractionDigits: 2,
                    })}
                  </Typography>
                  <Typography variant="body2">
                    <strong>SMV:</strong>{" "}
                    {totals.smv.toLocaleString(undefined, {
                      maximumFractionDigits: 2,
                    })}
                  </Typography>
                </Box>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ overflowX: "auto" }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      {tableColumns.map((col) => (
                        <TableCell key={col} sx={{ whiteSpace: "nowrap" }}>
                          {col}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {items.map((row) => (
                      <TableRow key={row.id}>
                        {tableColumns.map((col) => (
                          <TableCell
                            key={col}
                            sx={{ whiteSpace: "nowrap" }}
                            align="right"
                          >
                            {formatValue(row[col])}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </AccordionDetails>
          </Accordion>
        );
      })}
    </div>
  );
};

export default GroupedStrategyView;
